import styles from "./Card.module.css";

import { Button, Link } from "../Actions";
import NextLink from "next/link";

import Tick from "../../assets/Tick.svg";
import { useIsHolder } from "@/hooks/isHolder";

type CardProp = {
  title: string;
  amount: number | string | null;
  cost: number | string;
  features: string[];
  currency?: string;
  active: boolean;
  href: string;
  linkText: string;
  note?: string;
  availableText?: string;
  costClassName?: string;
  tokenId?: number;
  showCoopOwnerShip: boolean;
  discountText?: React.ReactNode | string;
  oldCost?: number;
  subscriberPass?: boolean;
  listTitle?: string;
  pillLink?: string;
  holderLink?: { label: string; href: string };
};

const getAmountText = (amountAvailable: number | string | null) => {
  if (!amountAvailable) return null;
  if (typeof amountAvailable === "number") {
    return " Max Supply";
  }
  return " Available";
};
export const Card: React.FC<CardProp> = ({
  title,
  amount,
  cost,
  features,
  currency,
  active,
  href,
  linkText,
  note,
  costClassName = "",
  tokenId = 3,
  showCoopOwnerShip,
  discountText,
  oldCost,
  subscriberPass,
  listTitle,
  pillLink,
  holderLink
}) => {
  const { address, isHolder } = useIsHolder();

  const featureList = features.map((feature, i) => (
    <li key={feature} className={`${styles.feature} flex gap-2`}>
      <Tick width={15} stroke="#388697" />
      {feature}
    </li>
  ));

  const Span: React.FC<any> = ({ children, className }) => (
    <span className={className}>{children}</span>
  );
  const Pill = pillLink ? NextLink : Span;

  return (
    <div
      className={`p-6 tab:p-4 m-4 rounded-3xl flex border-2 border-coinage-purple ${styles.card} max-w-[400px]`}>
      <div className={`${styles.textContainer} flex flex-col`}>
        <p className={`mb-2 ${styles.title} subhead2`}>
          {title} membership pass
        </p>
        <p
          className={`mb-4 ${styles.link} mono2 min-h-[40px] mb-[15px] ${
            subscriberPass ? "mb-[23px]" : ""
          }`}>
          <span className={amount == "∞" ? styles.infinitySign : ""}>
            {amount}
          </span>

          {amount == "∞" && <span className="ml-[10px]">Max Supply</span>}

          {getAmountText(tokenId === 3 ? null : amount)}
          {showCoopOwnerShip && (
            <span className="mt-2 block caption text-coinage-orange !font-bold">
              Own the Coinage Co-op.
            </span>
          )}
        </p>
        <p
          className={`${
            styles.cost
          } display3 text-coinage-blue ${costClassName} ${
            subscriberPass ? "mb-[15px]" : ""
          }`}>
          {oldCost && (
            <span className="text-coinage-gray line-through mr-2 font-thin">
              {oldCost}
            </span>
          )}
          {cost} {currency ? <span className={styles.currency}>ETH</span> : ""}{" "}
        </p>

        {discountText && (
          <Pill
            target="_blank"
            href={pillLink ?? ""}
            className={`body-3 text-[12px] rounded-2xl !w-fit px-[10px] my-[15px] py-1 bg-coinage-orange text-white width-auto ${
              subscriberPass ? "mb-[23px]" : ""
            }`}>
            {discountText}
          </Pill>
        )}
        <p className={`${styles.whatYouGet} mt-auto body2 text-coinage-blue`}>
          {listTitle}
        </p>
        <ul className={`${styles.list} body2 mb-5`}>{featureList}</ul>
        <div className={"w-full flex justify-center"}>
          {note && (
            <p
              className={`color-[#1E1C1BA6] opacity-60 mb-2 text-[12px] leading-[18px] ${styles.note}`}>
              Register for a spot on our access list{" "}
              <span className="bold">and</span> the chance to win a free
              membership pass.
            </p>
          )}
          {active ? (
            isHolder && holderLink ? (
              <Link
                analyticsProperty="NFT"
                modifier={"primary"}
                href={holderLink.href}>
                {holderLink.label}
              </Link>
            ) : (
              <Link analyticsProperty="NFT" modifier={"primary"} href={href}>
                {linkText}
              </Link>
            )
          ) : (
            <Button
              className="!w-full"
              analyticsProperty="NFT"
              modifier={"primary"}
              disabled={true}>
              {linkText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
